/**
 * The dialog in front of an action that cannot be taken back.
 *
 * `consequence` is required by the type, and that is the whole point of this
 * component existing rather than each page assembling a Modal with two buttons.
 * "Are you sure?" asks a question the person has already answered by clicking;
 * all it measures is whether they will click twice. What actually changes an
 * outcome is telling them the thing they did not know: that archiving a project
 * stops Kura answering its members, that removing a staff account reassigns
 * nothing and leaves eleven cases without an owner, that a broadcast already
 * sent cannot be unsaid.
 *
 * `confirmLabel` is a verb for the same reason. A row of "Cancel / OK" makes
 * the reader hold in their head which one the dialog was about; "Cancel /
 * Archive project" does not.
 *
 * `confirmPhrase` is the heavier gate, for the small number of actions that
 * destroy other people's records: type the name to proceed. It is deliberately
 * not the default. Used on everything it becomes a reflex like every other
 * confirmation, and then it protects nothing while costing every user time.
 */

import { useId, useState } from 'react'
import type { ReactNode } from 'react'
import { AlertIcon } from '@/components/icons/Icons'
import { Button, TextInput } from '@/components/primitives'
import type { Tone } from '@/components/primitives'
import { Modal } from '../Modal/Modal'
import styles from './ConfirmDialog.module.css'

export interface ConfirmDialogProps {
  open: boolean
  onCancel(): void
  onConfirm(): void
  title: string
  /**
   * What will happen if they go ahead, stated plainly. Required.
   *
   * Write the effect on the world, not a restatement of the button: "Members
   * stop receiving answers from this project's knowledge immediately. Cases and
   * conversations are kept."
   */
  consequence: ReactNode
  /** A verb phrase naming the action. Never "OK", never "Yes". */
  confirmLabel: string
  cancelLabel?: string
  /**
   * Two of the five names in `primitives/tone.ts`, and it takes them from there
   * rather than inventing a `'default'` of its own.
   *
   * The rest of the console maps a domain value to a `Tone` once and passes it
   * through unchanged; a component whose quiet state is spelt `default` while
   * every other component spells it `neutral` is one a caller has to look up.
   * Only these two are offered because there is no such thing as a confirmation
   * that is going well.
   */
  tone?: Extract<Tone, 'danger' | 'neutral'>
  /** When set, the confirm control stays disabled until this is typed exactly. */
  confirmPhrase?: string
  /** Disables the confirm control while the request is in flight. */
  busy?: boolean
}

export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  consequence,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'danger',
  confirmPhrase,
  busy = false,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('')
  const [wasOpen, setWasOpen] = useState(open)
  const phraseId = useId()

  /*
   * Cleared whenever the dialog opens or closes. Without this, cancelling a
   * deletion and then opening the dialog for a different record arrives with
   * the previous record's name already in the box, and the gate is standing
   * open for the wrong project.
   *
   * Adjusted during render rather than in an effect. An effect would let one
   * frame paint with the stale text in the field and the confirm button already
   * enabled, which is a frame in which the whole point of the gate is missing.
   * React re-runs this component immediately and never commits the intermediate
   * result, so nothing is shown from it.
   */
  if (open !== wasOpen) {
    setWasOpen(open)
    setTyped('')
  }

  const locked = confirmPhrase !== undefined && typed.trim() !== confirmPhrase

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      /* A misplaced click on the scrim should not be able to close the one
         dialog whose job is to slow a decision down. Escape and Cancel are both
         still there, so nobody is stuck. */
      dismissOnScrim={false}
      /*
        No `initialFocusRef`, so focus lands on the panel and a screen reader
        announces the title and the consequence before it announces any button.
        On this dialog above all others that order is the point: focusing
        Cancel would announce "Cancel, button" to somebody who has not yet been
        told what they are cancelling, and focusing Confirm would put a
        destructive action one Enter away from a keypress that was meant for
        the page behind.
      */
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          {/*
            `loading` rather than `disabled` while the request is in flight.
            Button's own header sets out why: a disabled button leaves the tab
            order, so the keyboard user who just pressed Enter has their focus
            dropped to the top of the document at the exact moment they are
            waiting to find out whether the thing worked. `disabled` is right
            for the phrase gate, where the control genuinely is not available
            yet.
          */}
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={locked}
            loading={busy}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        {tone === 'danger' ? (
          <span className={styles.mark} aria-hidden>
            <AlertIcon size={20} />
          </span>
        ) : null}
        <div className={styles.text}>
          <p className={styles.consequence}>{consequence}</p>

          {confirmPhrase === undefined ? null : (
            <div className={styles.gate}>
              <label className={styles.gateLabel} htmlFor={phraseId}>
                Type <span className={styles.phrase}>{confirmPhrase}</span> to confirm
              </label>
              <TextInput
                id={phraseId}
                className={styles.gateInput}
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                /* A password manager offering to fill a project's name here,
                   and a phone capitalising its first letter, both produce a
                   string that does not match and a person who cannot see why. */
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
